import { useId, useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Info } from 'lucide-react'
import { cn } from '@/utils'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { STT_LANGUAGES, type STTLanguage } from '@/constants/sttLanguages'

export type NullSentinel = 'use-account-default' | 'use-persona-default'

const NULL_SENTINEL_LABELS: Record<NullSentinel, string> = {
  'use-account-default': 'Use account default',
  'use-persona-default': 'Use persona default',
}

export interface STTLanguagePickerProps {
  value: string | null
  onChange: (code: string | null) => void
  /** When set, a "Use … default" option that maps to null is shown */
  nullSentinel?: NullSentinel
  /** When set, only languages with these codes are listed */
  restrictToCodes?: string[]
  /** Extra className for the trigger button */
  className?: string
  /** Disable the picker */
  disabled?: boolean
  /** Custom placeholder when value is null and no nullSentinel */
  placeholder?: string
}

const MULTI_INFO_TOOLTIP =
  'Auto-detect supports English, Spanish, French, German, Hindi, Russian, ' +
  'Portuguese, Japanese, Italian, Dutch. For other languages, pick the language explicitly.'

export function STTLanguagePicker({
  value,
  onChange,
  nullSentinel,
  restrictToCodes,
  className,
  disabled,
  placeholder,
}: STTLanguagePickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const listboxId = useId()

  const filteredLanguages = useMemo<STTLanguage[]>(() => {
    const base = restrictToCodes
      ? STT_LANGUAGES.filter((l) => restrictToCodes.includes(l.code))
      : [...STT_LANGUAGES]
    if (!search.trim()) return base
    const q = search.toLowerCase()
    return base.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q),
    )
  }, [restrictToCodes, search])

  const grouped = useMemo(() => {
    const auto = filteredLanguages.filter((l) => l.group === 'auto')
    const common = filteredLanguages.filter((l) => l.group === 'common')
    const all = filteredLanguages.filter((l) => l.group === 'all')
    return { auto, common, all }
  }, [filteredLanguages])

  const currentLabel = useMemo(() => {
    if (value === null && nullSentinel) return NULL_SENTINEL_LABELS[nullSentinel]
    if (value === null) return placeholder ?? 'Select language'
    // Search all languages (not just filtered) so the label always resolves
    const found = STT_LANGUAGES.find((l) => l.code === value)
    return found?.name ?? value
  }, [value, nullSentinel, placeholder])

  const handleSelect = (code: string | null) => {
    onChange(code)
    setOpen(false)
    setSearch('')
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSearch('')
  }

  const renderItem = (lang: STTLanguage) => (
    <button
      key={lang.code}
      type="button"
      role="option"
      aria-selected={value === lang.code}
      onClick={() => handleSelect(lang.code)}
      className={cn(
        'flex w-full items-center px-3 py-2 text-sm text-left',
        'hover:bg-accent',
        'focus:outline-hidden focus:bg-accent',
        value === lang.code && 'font-medium',
      )}
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4 shrink-0',
          value === lang.code ? 'opacity-100 text-primary' : 'opacity-0',
        )}
      />
      <span className="flex-1 truncate">{lang.name}</span>
      {lang.nativeName !== lang.name && lang.nativeName !== '—' && (
        <span className="ml-2 text-xs text-muted-foreground shrink-0">{lang.nativeName}</span>
      )}
      {lang.code === 'multi' && (
        <span title={MULTI_INFO_TOOLTIP} className="ml-1 shrink-0">
          <Info className="h-3 w-3 text-muted-foreground" />
        </span>
      )}
    </button>
  )

  const renderGroup = (heading: string, langs: STTLanguage[]) => {
    if (langs.length === 0) return null
    return (
      <div key={heading} role="group" aria-label={heading}>
        <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide select-none">
          {heading}
        </div>
        {langs.map(renderItem)}
      </div>
    )
  }

  const hasResults =
    (nullSentinel && (!search.trim() || 'use account default'.includes(search.toLowerCase()))) ||
    filteredLanguages.length > 0

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          type="button"
          variant="secondary"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          disabled={disabled}
          className={cn('w-full justify-between font-normal border border-border', className)}
        >
          <span className="truncate">{currentLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-(--radix-popover-trigger-width) p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          id={listboxId}
          role="listbox"
          className="rounded-md"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false)
              setSearch('')
            }
          }}
        >
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <Input
              autoFocus
              placeholder="Search languages…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {/* Options */}
          <div className="max-h-64 overflow-y-auto py-1">
            {!hasResults && (
              <div className="px-3 py-4 text-sm text-center text-muted-foreground">
                No language found.
              </div>
            )}

            {/* Null sentinel option */}
            {nullSentinel && (!search.trim() || NULL_SENTINEL_LABELS[nullSentinel].toLowerCase().includes(search.toLowerCase())) && (
              <div role="group" aria-label="Default">
                <button
                  type="button"
                  role="option"
                  aria-selected={value === null}
                  onClick={() => handleSelect(null)}
                  className={cn(
                    'flex w-full items-center px-3 py-2 text-sm text-left',
                    'hover:bg-accent',
                    'focus:outline-hidden focus:bg-accent',
                    value === null && 'font-medium',
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === null ? 'opacity-100 text-primary' : 'opacity-0',
                    )}
                  />
                  {NULL_SENTINEL_LABELS[nullSentinel]}
                </button>
              </div>
            )}

            {renderGroup('Auto-detect', grouped.auto)}
            {renderGroup('Common languages', grouped.common)}
            {renderGroup('All languages', grouped.all)}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
